import React, {
    forwardRef,
    InputHTMLAttributes,
    useEffect,
    useId,
    useRef,
    useState,
} from 'react';
import { twMerge } from 'tailwind-merge';
import { useMergeRefs } from 'flow/hooks/useMergeRefs';
import BaseInput from './BaseInput';
import CharacterCount from './CharacterCount';

interface Props extends InputHTMLAttributes<HTMLInputElement> {
    label?: string;
    defaultValue?: string;
    /**
     * The hard cap, enforced by the native `maxLength`. Text-only types: the
     * attribute is a no-op on `number`/`date`, which would leave the counter
     * implying a limit nothing enforces.
     */
    maxLength: number;
    /** Length at which the `used/max` counter is revealed. */
    characterCountFrom: number;
    type?: 'text' | 'email' | 'password';
}

/**
 * A {@link BaseInput} that reveals a `used/max` counter near its limit. The
 * counter measures the same UTF-16 code units `maxLength` enforces, so the two
 * can never disagree.
 *
 * Like `BaseInput`, this must never write `value={...}` — it would break
 * uncontrolled callers whose value arrives after mount. `value` stays in
 * `props` and reaches the input through the spread; the effect below reads
 * `props.value` without destructuring it.
 */
const CharacterCountInput = forwardRef<HTMLInputElement, Props>(
    (
        { className = '', maxLength, characterCountFrom, onChange, ...props },
        ref,
    ) => {
        const inputRef = useRef<HTMLInputElement>(null);
        const setRefs = useMergeRefs<HTMLInputElement>(ref, inputRef);
        const characterCountId = `${useId()}-character-count`;

        // `null` means "below the threshold, nothing to show". Re-rendering is
        // then skipped for free: every keystroke under the threshold sets
        // `null` over `null`, which React bails out of.
        const [lengthToShow, setLengthToShow] = useState<number | null>(null);

        const trackLength = (next: number) =>
            setLengthToShow(next >= characterCountFrom ? next : null);

        // Read the length off the element rather than off a prop: the field is
        // uncontrolled in some call sites, and its value can still arrive late
        // (a `defaultValue` fetched after mount).
        useEffect(() => {
            trackLength(inputRef.current?.value.length ?? 0);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [props.value, props.defaultValue]);

        const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            trackLength(event.target.value.length);
            onChange?.(event);
        };

        const showCharacterCount = lengthToShow !== null;

        return (
            <BaseInput
                // Spread first: `value` must stay absent unless the caller
                // passed one, and everything below is ours to own.
                {...props}
                ref={setRefs}
                maxLength={maxLength}
                // Reserve room so the counter never sits on top of the text.
                // `120/120` is ~48px at text-xs, plus the pe-3 inset.
                className={twMerge(className, showCharacterCount && 'pe-18')}
                aria-describedby={
                    showCharacterCount ? characterCountId : undefined
                }
                // A value saved before the limit existed can still be longer
                // than `maxLength`, which only constrains typing.
                aria-invalid={
                    showCharacterCount && lengthToShow > maxLength
                        ? true
                        : undefined
                }
                trailingSlot={
                    showCharacterCount ? (
                        <CharacterCount
                            id={characterCountId}
                            length={lengthToShow}
                            maxLength={maxLength}
                        />
                    ) : null
                }
                onChange={handleChange}
            />
        );
    },
);

export default CharacterCountInput;
